import React, { useState } from 'react';
import { Film, LayoutGrid, Palette, Settings } from 'lucide-react';
import { useSlotScreenGeneratorStore } from '@store/slotScreenGeneratorStore';
import { usePanelStore } from '@store/panelStore';
import { Panel } from '../panels/Panel';
import { VideoCompiler } from './VideoCompiler';
import { ScreenBuilderTab } from './ScreenBuilderTab';
import { IdeaChat } from './IdeaChat';
import { SystemPromptDrawer } from './SystemPromptDrawer';
import { useRestoreScreenImages } from '../../hooks/useRestoreScreenImages';
import { useRestoreIdeaCanvasImages } from '../../hooks/useRestoreIdeaCanvasImages';

type SectionTab = 'screens-video' | 'screen-builder' | 'canvas';

export function SlotScreenGeneratorPanel() {
  useRestoreScreenImages();
  useRestoreIdeaCanvasImages();
  const panel = usePanelStore(s => s.panels['slot-screen-generator']);
  const activeSection = useSlotScreenGeneratorStore(s => s.activeSection);
  const setActiveSection = useSlotScreenGeneratorStore(s => s.setActiveSection);
  const [showSettings, setShowSettings] = useState(false);

  return (
    <Panel panel={panel}>
      <div className="flex flex-col h-full bg-zinc-900 text-zinc-200 relative">
        {/* Tab Bar */}
        <div className="flex items-center gap-1 px-2 py-1.5 border-b border-zinc-800 bg-zinc-900/80 flex-shrink-0">
          {([
            { id: 'screens-video' as SectionTab, label: 'Storyboard', icon: Film },
            { id: 'screen-builder' as SectionTab, label: 'Screen Builder', icon: LayoutGrid },
            { id: 'canvas' as SectionTab, label: 'Idea Canvas', icon: Palette },
          ]).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium transition-colors ${
                activeSection === tab.id
                  ? 'bg-zinc-700 text-zinc-100'
                  : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
              }`}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
          <div className="flex-1" />
          <button
            onClick={() => setShowSettings(true)}
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 rounded transition-colors"
          >
            <Settings size={12} />
            System Prompt
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden relative">
          {activeSection === 'screens-video' && <VideoCompiler />}
          {activeSection === 'screen-builder' && <ScreenBuilderTab />}
          {activeSection === 'canvas' && <IdeaChat />}
        </div>
      </div>

      <SystemPromptDrawer isOpen={showSettings} onClose={() => setShowSettings(false)} />
    </Panel>
  );
}
